<template>
  <v-list-item :to="pageExists ? `/content/pages/${page.id}` : null" class="items-center d-flex">
    <flag-pip :flag="language.flag" :text="language.name" show-tooltip class="mr-2" />
    <v-tooltip color="grey darken-4" bottom small>
      <template #activator="{ on }">
        <base-chip :color="color" :icon="icon" label small class="px-1 mr-2" v-on="on" />
      </template>
      <span>{{ tooltipText }}</span>
    </v-tooltip>
    <v-tooltip v-if="pageExists && page.is_revised" color="grey darken-4" bottom small>
      <template #activator="{ on }">
        <v-icon color="grey darken-2" class="mr-2" v-on="on">rate_review</v-icon>
      </template>
      <span>Has revised changes</span>
    </v-tooltip>
    <base-chip :text="site.hostname" label small dark color="grey darken-2" class="mr-2" />
    <v-tooltip left>
      <template #activator="{ on }">
        <div v-show="showTranslateButton" class="ml-auto" v-on="!canTranslate && on">
          <v-btn
            small
            color="secondary"
            class="ml-auto"
            depressed
            :disabled="isLoading || !canTranslate"
            :loading="isLoading"
            @click.prevent="showTranslationModal = true"
          >
            Translate
          </v-btn>
        </div>
      </template>
      <span>Cannot translate until the following actions are performed:</span>
      <ul>
        <li v-if="!canUpdate">User must have update permissions</li>
        <li v-if="hasChanges">All changes must be saved or reset</li>
        <li v-if="parentPage.wp_publish_status !== 'publish'">Status must be set to Published</li>
        <li v-if="!parentPage.wp_post_id">Post ID must be set</li>
        <li v-if="parentPage.is_revised">Page must not contain revised changes</li>
      </ul>
    </v-tooltip>
    <translate-modal
      v-model="showTranslationModal"
      :parent-page="parentPage"
      :translating-pages="pageToTranslate"
      :is-disabled="isLoading"
      @modal-confirm="onTranslateConfirm"
    />
  </v-list-item>
</template>

<script>
// Services
import { pages } from '@/services/offers'
// Components
import BaseChip from '@/components/chips/base-chip'
import FlagPip from '@/views/news-articles/components/flag-pip'
import TranslateModal from '@/views/content/pages/components/translate-modal'

export default {
  name: 'PageLanguageItem',
  components: { BaseChip, FlagPip, TranslateModal },
  props: {
    hasChanges: {
      type: Boolean,
      default: false,
    },
    language: {
      type: Object,
      required: true,
    },
    site: {
      type: Object,
      required: true,
    },
    parentPage: {
      type: Object,
      required: true,
    },
    pages: {
      type: Array,
      required: true,
    },
    large: {
      type: Boolean,
      default: false,
    },
    canUpdate: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isLoading: false,
      showTranslationModal: false,
    }
  },
  computed: {
    page() {
      return this.pages.find(({ site_id, language_id }) => site_id === this.site.id && language_id === this.language.id)
    },
    pageExists() {
      return !!this.page
    },
    pageToTranslate() {
      // If the page does not exist, reconstruct the object we need with language and site.
      return this.page ? [this.page] : [{ language: this.language, site: this.site, is_revised: false }]
    },
    canTranslate() {
      if (!this.canUpdate) return false
      if (this.hasChanges) return false
      if (this.parentPage.wp_publish_status !== 'publish') return false
      if (!this.parentPage.wp_post_id) return false
      if (this.parentPage.language.code !== 'en') return false
      if (this.parentPage.is_revised) return false
      return true
    },
    tooltipText() {
      if (!this.pageExists) return 'This page does not exist'
      if (this.page.wp_publish_status === 'publish') return 'This page is published'
      return 'This page is in draft'
    },
    color() {
      if (!this.pageExists) return 'error'
      if (this.page.wp_publish_status === 'publish') return 'success'
      return 'warning'
    },
    icon() {
      if (!this.pageExists) return 'close'
      if (this.page.wp_publish_status === 'publish') return 'check'
      return 'create'
    },
    showTranslateButton() {
      return this.parentPage.language.code === 'en' && this.language.code !== 'en'
    },
  },
  methods: {
    async onTranslateConfirm() {
      await this.translate(true)
      this.showTranslationModal = false
    },
    async translate() {
      this.isLoading = true

      try {
        const { id } = await pages.translate(this.parentPage.id, {
          site_id: this.site.id,
          language_id: this.language.id,
        })
        this.$notify({
          type: 'success',
          text: 'Successfully translated page',
          data: {
            route: { name: 'content.pages.edit', params: { id } },
          },
          duration: 7000,
        })
        this.$emit('refresh')
      } catch (error) {
        this.$notify({ type: 'error', text: 'Unable to translate page.', data: { error }, duration: -1 })
      }
      this.isLoading = false
    },
  },
}
</script>
